import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Textarea } from '../../components/ui/textarea';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Receipt, FileText, Stethoscope, Loader2, Zap, ShieldAlert, Apple } from 'lucide-react';
import { ClinicalSafetyGate } from '../../care/ClinicalSafetyGate';
import { POSCustomerFields } from './POSCustomerFields';
import { POSReceiptOptions } from './POSReceiptOptions';
import { POSLoyaltyPanel } from './POSLoyaltyPanel';
import { POSCashTender } from './POSCashTender';
import { FileUploadField } from '../../components/FileUploadField';
import { CounselingSlipActions } from './CounselingSlipActions';
import { fmtCurrency, fmtNumber, getCurrencySymbol } from '../../lib/localeUtils';

export const POSCheckoutDialog = ({
  open, onOpenChange, cart, subtotal, total,
  customer, payment, partners, loyalty, receipts, prescription,
  clinicalBlocked, onClinicalBlockedChange, processing, onConfirm,
  interactionAlerts = [],
  allergyAlerts = [],
  foodAlerts = [],
  selectedPatientId,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="w-[calc(100%-2rem)] max-w-5xl max-h-[92dvh] p-0 flex flex-col gap-0 overflow-hidden" data-testid="checkout-dialog" closeProps={{ 'data-testid': 'checkout-close-button' }}>
      <DialogHeader className="px-5 py-4 border-b shrink-0 text-left">
        <DialogTitle className="flex items-center gap-2 flex-wrap">
          <Receipt className="h-4 w-4" /> Complete Sale
          {allergyAlerts.length > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-800 px-2 py-0.5 text-[10px] font-bold" data-testid="checkout-allergy-badge">
              <ShieldAlert className="h-3 w-3" />ALLERGY
            </span>
          )}
          {interactionAlerts.length > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-semibold" data-testid="checkout-interaction-badge">
              <Zap className="h-3 w-3" />{interactionAlerts.length} interaction{interactionAlerts.length > 1 ? 's' : ''}
            </span>
          )}
          {foodAlerts.length > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[10px] font-semibold" data-testid="checkout-food-otc-badge">
              <Apple className="h-3 w-3" />{foodAlerts.length} counseling point{foodAlerts.length > 1 ? 's' : ''}
            </span>
          )}
        </DialogTitle>
        <DialogDescription data-testid="checkout-item-count">{cart.length} item{cart.length === 1 ? '' : 's'} · {cart.reduce((sum, item) => sum + Number(item.quantity || 0), 0)} units</DialogDescription>
      </DialogHeader>
      <div className="overflow-y-auto min-h-0 p-5" data-testid="checkout-scroll-body">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="checkout-columns">
        <section className="min-w-0 space-y-4" data-testid="checkout-customer-column">
        <h2 className="text-base font-semibold">Customer &amp; safety</h2>
        {/* Allergy alerts — critical, always visible, shown first */}
        {allergyAlerts.length > 0 && (
          <Alert className="border-2 border-rose-500 bg-rose-50" data-testid="checkout-allergy-alert">
            <ShieldAlert className="h-4 w-4 text-rose-600" />
            <AlertDescription className="text-xs text-rose-800 space-y-1">
              <p className="font-bold">ALLERGY ALERT — Verify before dispensing</p>
              {allergyAlerts.map((a) => (
                <p key={a.id}>{(a.drug_pair || []).join(' → ')}: {(a.description || '').slice(0, 120)}{(a.description?.length ?? 0) > 120 ? '…' : ''}</p>
              ))}
            </AlertDescription>
          </Alert>
        )}
        {/* Interaction alerts */}
        {interactionAlerts.length > 0 && (
          <Alert className="border-amber-400 bg-amber-50" data-testid="checkout-interaction-alert">
            <Zap className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-xs text-amber-800 space-y-1">
              <p className="font-semibold">Drug interaction{interactionAlerts.length > 1 ? 's' : ''} detected — review before confirming</p>
              {interactionAlerts.slice(0, 2).map((a) => (
                <p key={a.id} className="opacity-90">{(a.drug_pair || []).join(' + ')}: {(a.description || '').slice(0, 120)}{(a.description?.length ?? 0) > 120 ? '…' : ''}</p>
              ))}
              {interactionAlerts.length > 2 && <p className="opacity-70">+{interactionAlerts.length - 2} more</p>}
              <p className="opacity-60 text-[10px]">Non-blocking — pharmacist may proceed using clinical judgement.</p>
            </AlertDescription>
          </Alert>
        )}
        {/* Food / OTC counseling points */}
        {foodAlerts.length > 0 && (
          <Alert className="border-emerald-400 bg-emerald-50" data-testid="checkout-food-otc-alert">
            <Apple className="h-4 w-4 text-emerald-600" />
            <AlertDescription className="text-xs text-emerald-800 space-y-1">
              <p className="font-semibold">Counseling point{foodAlerts.length > 1 ? 's' : ''} — share with patient before dispensing</p>
              {foodAlerts.slice(0, 2).map((a) => (
                <p key={a.id} className="opacity-90">{(a.drug_pair || []).join(' ↔ ')}: {(a.description || '').slice(0, 120)}{(a.description?.length ?? 0) > 120 ? '…' : ''}</p>
              ))}
              {foodAlerts.length > 2 && <p className="opacity-70">+{foodAlerts.length - 2} more</p>}
              <p className="opacity-60 text-[10px]">Non-blocking — drug-food & OTC combination counseling.</p>
              <CounselingSlipActions
                alerts={foodAlerts}
                patientId={selectedPatientId}
                cartNames={cart.map((i) => i.generic_name || i.product_name || '').filter(Boolean)}
                defaultPhone={customer?.phone}
                testIdPrefix="checkout-food-otc"
              />
            </AlertDescription>
          </Alert>
        )}
        {prescription.info && (
          <Alert className="border-primary/50 bg-primary/5" data-testid="checkout-prescription-info">
            <FileText className="h-4 w-4 text-primary" />
            <AlertDescription className="text-xs">
              <strong>Prescription Sale</strong>
              {prescription.info.doctorName && ` • Dr. ${prescription.info.doctorName}`}
              {prescription.info.prescriptionDate && ` • Date: ${prescription.info.prescriptionDate}`}
            </AlertDescription>
          </Alert>
        )}

        <POSCustomerFields customer={customer} payment={payment} partners={partners} total={total} />

        <ClinicalSafetyGate phone={customer.phone} cart={cart} onBlockingChange={onClinicalBlockedChange} />

        {prescription.notes && (
          <div className="space-y-2">
            <Label className="flex items-center gap-1">
              <Stethoscope className="h-3 w-3" /> Drug Interaction Notes
            </Label>
            <Textarea
              value={prescription.notes}
              onChange={(e) => prescription.setNotes(e.target.value)}
              className="text-xs h-20 resize-none"
              placeholder="Notes from prescription analysis..."
              data-testid="prescription-notes-input"
            />
          </div>
        )}

        <FileUploadField
          files={prescription.attachments}
          onChange={prescription.setAttachments}
          category="prescription"
          max={5}
          label="Prescription scans & documents"
          testId="pos-attachments"
        />
        </section>
        <section className="min-w-0 space-y-4 md:border-l md:pl-6" data-testid="checkout-payment-column">
        <h2 className="text-base font-semibold">Order &amp; payment</h2>
        <div className="max-h-36 overflow-y-auto space-y-2" data-testid="checkout-order-items">
          {cart.map(item => <div key={item.product_id} className="flex justify-between gap-3 text-sm" data-testid={`checkout-order-item-${item.product_id}`}>
            <span className="min-w-0 break-words">{item.quantity} × {item.product_name || item.name}</span>
            <span className="shrink-0 tabular-nums">{fmtCurrency(item.quantity * item.unit_price)}</span>
          </div>)}
        </div>
        <div className="space-y-2">
          <Label htmlFor="checkout-discount">Discount ({getCurrencySymbol()})</Label>
          <Input id="checkout-discount" type="number" value={payment.discount}
            onChange={(e) => payment.setDiscount(parseFloat(e.target.value) || 0)}
            min={0} max={subtotal} data-testid="discount-input" />
        </div>
        <POSLoyaltyPanel loyalty={loyalty} total={total} />
        <Separator />
        <div className="space-y-1.5">
          <div className="flex justify-between text-sm" data-testid="checkout-subtotal"><span>Subtotal</span><span>{fmtCurrency(subtotal)}</span></div>
          {payment.discount > 0 && (
            <div className="flex justify-between text-sm text-destructive" data-testid="checkout-discount-total"><span>Discount</span><span>-{fmtCurrency(payment.discount)}</span></div>
          )}
          {loyalty.discount > 0 && (
            <div className="flex justify-between text-sm text-emerald-700" data-testid="loyalty-discount-line">
              <span>Loyalty ({fmtNumber(loyalty.effectivePoints ?? loyalty.redeemPoints)} pts)</span>
              <span>-{fmtCurrency(loyalty.discount)}</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-lg" data-testid="checkout-total"><span>Total</span><span>{fmtCurrency(total)}</span></div>
        </div>

        {payment.method === 'cash' && (
          <POSCashTender cashReceived={payment.cashReceived} setCashReceived={payment.setCashReceived} total={total} />
        )}
        <POSReceiptOptions receipts={receipts} customerPhone={customer.phone} />
        </section>
      </div>
      </div>
      <div className="border-t bg-muted/30 px-5 py-4 flex flex-wrap items-center justify-between gap-3 shrink-0" data-testid="checkout-footer">
        <span className="text-lg font-bold tabular-nums" data-testid="checkout-amount-due">{fmtCurrency(total)}</span>
        <Button className="min-h-11 h-auto py-3 whitespace-normal sm:min-w-52 font-semibold flex-1 sm:flex-none" onClick={onConfirm} disabled={processing || clinicalBlocked} data-testid="confirm-checkout-btn">
          {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {clinicalBlocked ? 'Resolve clinical safety review to continue' : 'Confirm Sale'}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);
